import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { ThemeBreakpointIndexEnum } from '@inter-ikea-kompis/enums';
import { ThemeBreakpointObserver } from '@inter-ikea-kompis/themes';
import type { ITheme } from '@inter-ikea-kompis/types';
import { IframeUtility, IframeVisibleAreaObserver } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { ButtonSizeEnum } from '@inter-ikea-kompis/component-button';
import { Text } from '@inter-ikea-kompis/component-text';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';

import PromptButtonTypeEnum from '../../enums/PromptButtonTypeEnum.js';
import PromptStyle from './PromptStyle.js';

/**
 * @deprecated This component has been deprecated, see Kompis page for more info.
 *
 * Prompt component.
 *
 * @event buttonClick When a button is clicked.
 * @event animationEnd When the animation ends.
 */

export default class Prompt extends KompisElement {
	// Can be open, closed, opening and closing

	// Sets the prompt to fullscreen. Only affects mobile
	@property({ type: Boolean })
	public fullscreen = false;

	// Heading for the prompt
	@property({ type: String })
	public heading: string | null = null;

	// Label for the primary button
	@property({ type: String })
	public primaryButtonLabel: string | null = null;

	// Label for the secondary button
	@property({ type: String })
	public secondaryButtonLabel: string | null = null;

	// True if sticky should be disabled.
	@property({ type: Boolean })
	public disableIframeSticky = false;

	// Override z-index of the prompt.
	@property({ type: Number })
	public zIndex = 500;

	// Hide or show prompt with transitions.
	@property({ type: Boolean })
	public visible = false;

	// Compact placing of buttons.
	@property({ type: Boolean })
	public compact = false;

	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	private isAnimating = false;
	private isRendered = false;
	private top = 0;
	private bottom = 0;
	private breakpointObserver = new ThemeBreakpointObserver(() => this.requestUpdate());
	private iframeVisibleAreaObserver =
		IframeUtility.isInsideIframe() && IframeUtility.isParentWindowAccessible()
			? new IframeVisibleAreaObserver({
					debounceTime: 100,
					overflow: true,
					callback: (area) => {
						this.top = area.top;
						this.bottom = area.bottom;
						this.requestUpdate();
					}
				})
			: null;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		if (!this.visible && !this.isAnimating) {
			return null;
		}

		return html`
			<style>
				${PromptStyle.getStyle({
					isAnimating: this.isAnimating,
					isFullscreen: this.fullscreen,
					zIndex: this.zIndex,
					top: this.top,
					bottom: this.bottom,
					theme: this.theme,
					visible: this.visible,
					compact: this.compact
				})}
			</style>
			<div class="backdrop"></div>
			${this.getIntersection()}
		`;
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.isRendered = false;
		this.isAnimating = false;
		if (this.iframeVisibleAreaObserver) {
			this.iframeVisibleAreaObserver.disconnect();
		}
		this.breakpointObserver.disconnect();
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('kompis-text', Text);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('visible') && this.isRendered) {
			this.isAnimating = true;
		}

		if (changedProperties.has('disableIframeSticky')) {
			if (this.disableIframeSticky) {
				this.top = 0;
				this.bottom = 0;
			}
		}

		if (this.iframeVisibleAreaObserver) {
			if (
				this.disableIframeSticky ||
				!IframeUtility.isInsideIframe() ||
				!IframeUtility.isParentWindowAccessible()
			) {
				this.iframeVisibleAreaObserver.disconnect();
			} else {
				this.iframeVisibleAreaObserver.observe();
				const area = this.iframeVisibleAreaObserver.getArea();
				this.top = area.top;
				this.bottom = area.bottom;
			}
		}
		if (changedProperties.has('theme')) {
			this.breakpointObserver.observe(this.theme);
		}
		this.isRendered = true;
		return true;
	}

	/**
	 * The intersection between iframe and visible area.
	 *
	 * @returns - Html.
	 */
	private getIntersection(): RenderResult {
		return html`
			<div class="intersection">
				<div class="lightbox" @animationend="${this.onAnimationEnd.bind(this)}">
					<div class="scrollContainer">
						<div class="header">
							<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.headingMedium}"
								>${this.heading}</kompis-text
							>
						</div>
						<div class="body"><slot></slot></div>
					</div>
					<div class="footer">
						${this.getPrimaryButton()}${this.getButtonDivider()}${this.getSecondaryButton()}
					</div>
				</div>
			</div>
		`;
	}

	/**
	 * Listener for animationend.
	 */
	private onAnimationEnd(): void {
		this.isAnimating = false;
		if (this.visible) {
			this.dispatchEvent(
				new CustomEvent('animationEnd', { bubbles: true, detail: { type: 'open' } })
			);
		} else {
			this.dispatchEvent(
				new CustomEvent('animationEnd', { bubbles: true, detail: { type: 'close' } })
			);
		}
		this.requestUpdate();
	}

	/**
	 * @returns Primary button HTMl.
	 */
	private getPrimaryButton(): RenderResult {
		if (!this.primaryButtonLabel) {
			return null;
		}

		const primaryVariant: ButtonVariant = 'primary';

		return html`
			<skapa-button
				class="primaryButton"
				size="${this.getButtonSize() === ButtonSizeEnum.small ? 'small' : 'medium'}"
				variant="${primaryVariant}"
				fluid
				@click="${() =>
					this.dispatchEvent(
						new CustomEvent('buttonClick', {
							bubbles: true,
							detail: { type: PromptButtonTypeEnum.primaryButton }
						})
					)}"
			>
				${this.primaryButtonLabel}
			</skapa-button>
		`;
	}

	/**
	 * @returns Secondary button HTMl.
	 */
	private getSecondaryButton(): RenderResult {
		if (!this.secondaryButtonLabel) {
			return null;
		}

		const secondaryVariant: ButtonVariant = 'secondary';

		return html`
			<skapa-button
				class="secondaryButton"
				size="${this.getButtonSize() === ButtonSizeEnum.small ? 'small' : 'medium'}"
				variant="${secondaryVariant}"
				fluid
				@click="${() =>
					this.dispatchEvent(
						new CustomEvent('buttonClick', {
							bubbles: true,
							detail: { type: PromptButtonTypeEnum.secondaryButton }
						})
					)}"
			>
				${this.secondaryButtonLabel}
			</skapa-button>
		`;
	}

	private getButtonDivider(): RenderResult {
		if (!this.secondaryButtonLabel) {
			return null;
		}

		return html`<div class="buttonDivider"></div>`;
	}

	/**
	 * @returns True if landscape layout.
	 */
	private getButtonSize(): ButtonSizeEnum {
		if (!this.compact || this.secondaryButtonLabel === null) {
			return ButtonSizeEnum.medium;
		}
		const currentBreakpointIndex = this.breakpointObserver.getBreakpointIndex();
		// If breakpointIndex is null, then return small button size
		if (!currentBreakpointIndex || currentBreakpointIndex <= ThemeBreakpointIndexEnum.small) {
			return ButtonSizeEnum.small;
		}
		return ButtonSizeEnum.medium;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'kompis-prompt': Prompt;
	}
}
